import type {EntityType} from 'src/types/ats-entities';
import type {
  Id,
  EditableGraph,
  EditableNode,
  EditableNodeType,
} from 'src/types/chatbot';

import * as React from 'react';
import {useDispatch} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {createEditableNode} from 'src/components/conversation-builder/utils';
import {
  getAllEntities,
  getAllEntityAttributes,
} from 'src/action-creators/ats-entities';
import {validateNode} from './validation';

import Loading from 'src/components/lib/loading';
import Popover from 'src/components/conversation-builder/popover';
import QuestionEditor from './question';
import MessageEditor from './message';
import SummaryEditor from './summary';
import NotificationEditor from './notification';
import ConditionEditor from './condition';
import DispositionEditor from './disposition';
import SchedulerEditor from './scheduler';
import JobMatchEditor from './job-match';


export function NodeEditor({
  type,
  defaultNode,
  graph,
  onSubmit,
  edgeId,
  ...props
}: {
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: EditableNode) => unknown;
  defaultNode?: EditableNode;
  type: EditableNodeType;
  actionName: 'add' | 'edit';
  graph: EditableGraph;
  edgeId?: Id;
  readOnly: boolean;
}) {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const loading = useAsyncDependency(
    () =>
      Promise.all([
        dispatch(getAllEntities()),
        dispatch(getAllEntityAttributes()),
      ]),
    [],
  );

  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
  const handleSubmit = (node) => {
    if (validateNode(node, graph)) {
      setHasSubmitted(true);
    } else {
      onSubmit(node);
    }
  };

  if (loading) {
    return (
      <Popover titleText="" onClose={props.onClose}>
        <Loading />
      </Popover>
    );
  }

  const node =
    defaultNode ||
    createEditableNode(
      t,
      type,
      graph,
      edgeId ? graph.edges.get(edgeId) : undefined,
    );

  const nodeProps = {
    ...props,
    graph,
    hasSubmitted,
    onSubmit: handleSubmit,
  } as const;

  switch (node.type) {
    case 'message':
      return (
        <MessageEditor {...nodeProps} edgeId={edgeId} defaultNode={node} />
      );
    case 'file_upload':
      return (
        <MessageEditor {...nodeProps} edgeId={edgeId} defaultNode={node} />
      );
    case 'question':
      return (
        <QuestionEditor {...nodeProps} edgeId={edgeId} defaultNode={node} />
      );
    case 'summary':
      return (
        <SummaryEditor {...nodeProps} edgeId={edgeId} defaultNode={node} />
      );
    case 'notification':
      return (
        <NotificationEditor {...nodeProps} edgeId={edgeId} defaultNode={node} />
      );
    case 'condition':
      return <ConditionEditor {...nodeProps} defaultNode={node} />;
    case 'disposition':
      return (
        <DispositionEditor {...nodeProps} edgeId={edgeId} defaultNode={node} />
      );
    case 'scheduler':
      return (
        <SchedulerEditor {...nodeProps} defaultNode={node} graph={graph} />
      );
    case 'job_match':
      return (
        <JobMatchEditor
          {...nodeProps}
          defaultNode={node}
          edgeId={edgeId}
          graph={graph}
        />
      );
    default:
      return null;
  }
}

export function useAsyncDependency(
  callback: () => Promise<unknown>,
  deps: unknown[],
): boolean {
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);

    callback().then(() => {
      setLoading(false);
    });
  }, deps);

  return loading;
}
